import React, { useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import {
  Search, Download, Plus, QrCode, MoreVertical, Eye, Wallet, Trash2, X,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, Check,
  Camera, Upload, ChevronDown, Users,
} from "lucide-react";
import {
  DEMO_SOCIOS, DEMO_FEATURED, DEMO_TOTAL, DEMO_ACTIVOS, PREREGISTER_URL,
} from "../../dev/sociosDemoData";
import "./SociosPage.css";

/*
 * SociosPage — content-only "Socios" (members) section, translated from Figma
 * (ClubAgent - UI · section "Socios", node 3330:23074).
 *
 * Renders inside the real app shell (Layout → SidebarBest + HeaderNew), so this
 * file styles ONLY the page content. Scoped under .sc-page with the raw Figma
 * orange-rebrand tokens (this CRA build does not process Tailwind — the app uses
 * Bulma + per-component CSS; hence explicit resets to stay isolated from Bulma).
 *
 * Screens (internal `view` state, no routing):
 *   list    → Listado Socios: search / tabs / sort / table / pagination,
 *             per-row actions menu, right-side "Detalle socio" drawer.
 *   form    → Nuevo / Editar socio form (Datos personales · Contacto ·
 *             Documentación · Membresía) with light required-field validation.
 *   profile → Perfil socio (read-only) + "Eliminar socio".
 * Overlays: QR de preinscripción, ¿Eliminar socio?, Gestión de saldo, toast.
 *
 * Preview only (mock data). A real /socios route + API wiring is a follow-up.
 */

/* ─────────────── helpers ─────────────── */
const AVATAR_COLORS = ["#0ea5e9", "#8b5cf6", "#ec4899", "#f97316", "#10b981", "#ef4444", "#6366f1", "#14b8a6"];
const avatarColor = (seed) => AVATAR_COLORS[Math.abs(hash(seed)) % AVATAR_COLORS.length];
function hash(s = "") { let h = 0; for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i); return h; }
function initials(name = "") {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}
function formatEuro(n) {
  return "€" + Number(n).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseDate(dmy = "") {
  const [d, m, y] = dmy.split("/").map(Number);
  return new Date(y || 0, (m || 1) - 1, d || 1).getTime();
}
const ESTADO_LABEL = { activo: "Activo", caducado: "Caducado", suspendido: "Suspendido" };

function Avatar({ socio, size = 40, square = false }) {
  const style = { width: size, height: size, flex: `0 0 ${size}px`, borderRadius: square ? 8 : 9999 };
  if (socio.photo) return <img className="sc-avatar" src={socio.photo} alt={socio.name} style={style} />;
  return (
    <span className="sc-avatar" style={{ ...style, background: avatarColor(socio.name), fontSize: size * 0.36 }}>
      {initials(socio.name)}
    </span>
  );
}

const SORT_OPTIONS = [
  { key: "num_desc", label: "Nº Socio (mayor → menor)" },
  { key: "num_asc", label: "Nº Socio (menor → mayor)" },
  { key: "saldo_desc", label: "Saldo (mayor → menor)" },
  { key: "saldo_asc", label: "Saldo (menor → mayor)" },
  { key: "alta_desc", label: "Inscripción (recientes primero)" },
  { key: "alta_asc", label: "Inscripción (antiguas primero)" },
  { key: "cad_asc", label: "Caducidad (próximas primero)" },
  { key: "cad_desc", label: "Caducidad (lejanas primero)" },
];
const TABS = [
  { key: "todos", label: "Todos" },
  { key: "activo", label: "Activos" },
  { key: "caducado", label: "Caducados" },
  { key: "suspendido", label: "Suspendidos" },
];
const PAGE_SIZE = 12;

export default function SociosPage({ demo }) {
  const data = demo || {};
  const socios = data.socios || DEMO_SOCIOS;
  const featured = data.featured || DEMO_FEATURED;
  const total = data.total ?? DEMO_TOTAL;
  const activos = data.activos ?? DEMO_ACTIVOS;
  const preRegisterUrl = data.preRegisterUrl || PREREGISTER_URL;

  const [view, setView] = useState("list");          // list | form | profile
  const [editing, setEditing] = useState(null);      // socio being edited (null → nuevo)
  const [profileSocio, setProfileSocio] = useState(featured);

  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("todos");
  const [sort, setSort] = useState("num_asc");
  const [page, setPage] = useState(1);

  const [sortOpen, setSortOpen] = useState(false);
  const [menuFor, setMenuFor] = useState(null);       // socio id whose actions menu is open
  const [detail, setDetail] = useState(null);         // socio shown in the right drawer
  const [qrOpen, setQrOpen] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);   // socio pending deletion
  const [saldoFor, setSaldoFor] = useState(null);     // socio in the saldo dialog
  const [toast, setToast] = useState(null);

  function flash(msg, tone = "ok") {
    setToast({ msg, tone });
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setToast(null), 2600);
  }

  /* filter → sort → paginate */
  const filtered = useMemo(() => {
    let list = socios;
    if (tab !== "todos") list = list.filter((s) => s.estado === tab);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((s) => s.name.toLowerCase().includes(q) || String(s.number).includes(q) || (s.email || "").toLowerCase().includes(q));
    const arr = [...list];
    const by = {
      num_desc: (a, b) => b.number - a.number,
      num_asc: (a, b) => a.number - b.number,
      saldo_desc: (a, b) => b.saldo - a.saldo,
      saldo_asc: (a, b) => a.saldo - b.saldo,
      alta_desc: (a, b) => parseDate(b.alta) - parseDate(a.alta),
      alta_asc: (a, b) => parseDate(a.alta) - parseDate(b.alta),
      cad_asc: (a, b) => parseDate(a.caducidad) - parseDate(b.caducidad),
      cad_desc: (a, b) => parseDate(b.caducidad) - parseDate(a.caducidad),
    }[sort];
    return arr.sort(by);
  }, [socios, tab, query, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function openNew() { setEditing(null); setView("form"); }
  function openEdit(s) { setEditing(s); setView("form"); }
  function openProfile(s) { setProfileSocio(s); setView("profile"); setDetail(null); setMenuFor(null); }

  /* close popovers on outside click */
  const closePopovers = () => { setSortOpen(false); setMenuFor(null); };

  return (
    <div className="sc-page" onClick={closePopovers}>
      {view === "list" && (
        <ListView
          {...{ socios, total, activos, query, setQuery, tab, setTab, sort, setSort,
            sortOpen, setSortOpen, menuFor, setMenuFor, pageRows, pageCount, safePage, setPage,
            openNew, openProfile, setDetail, setQrOpen, setDeleteFor, setSaldoFor }}
        />
      )}
      {view === "form" && (
        <SocioForm
          socio={editing}
          onCancel={() => setView("list")}
          onSave={() => { setView("list"); flash(editing ? "Cambios guardados" : "Socio creado"); }}
        />
      )}
      {view === "profile" && (
        <SocioProfile
          socio={profileSocio}
          onBack={() => setView("list")}
          onEdit={() => openEdit(profileSocio)}
          onSaldo={() => setSaldoFor(profileSocio)}
          onDelete={() => setDeleteFor(profileSocio)}
        />
      )}

      {/* ── right-side Detalle socio drawer (over the list) ── */}
      {detail && (
        <DetailDrawer
          socio={detail}
          onClose={() => setDetail(null)}
          onSaldo={() => setSaldoFor(detail)}
          onProfile={() => openProfile(detail)}
        />
      )}

      {/* ── overlays ── */}
      {qrOpen && (
        <QrModal url={preRegisterUrl} onClose={() => setQrOpen(false)} onCopied={() => flash("Enlace copiado")} onRotated={() => flash("Nuevo QR generado")} />
      )}
      {deleteFor && (
        <DeleteDialog
          socio={deleteFor}
          onCancel={() => setDeleteFor(null)}
          onConfirm={() => { setDeleteFor(null); setDetail(null); if (view === "profile") setView("list"); flash("Socio eliminado", "danger"); }}
        />
      )}
      {saldoFor && (
        <SaldoDialog socio={saldoFor} onClose={() => setSaldoFor(null)} onDone={(msg) => { setSaldoFor(null); flash(msg); }} />
      )}
      {toast && (
        <div className={`sc-toast sc-toast--${toast.tone}`} role="status">
          <span className="sc-toast-dot" />{toast.msg}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ LIST ═══════════════ */
function ListView(props) {
  const {
    total, activos, query, setQuery, tab, setTab, sort, setSort, sortOpen, setSortOpen,
    menuFor, setMenuFor, pageRows, pageCount, safePage, setPage,
    openNew, openProfile, setDetail, setQrOpen, setDeleteFor, setSaldoFor,
  } = props;
  const stop = (e) => e.stopPropagation();

  return (
    <>
      {/* header */}
      <div className="sc-head">
        <div className="sc-head-left">
          <h1 className="sc-title">Listado Socios</h1>
          <span className="sc-counter"><Users size={14} /> {activos}/{total} socios</span>
        </div>
        <div className="sc-search sc-search--grow">
          <Search size={16} />
          <input placeholder="Buscar socio" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
        </div>
        <button className="sc-btn-outline" onClick={() => setQrOpen(true)}><QrCode size={16} /> QR preinscripción</button>
        <button className="sc-btn-outline"><Download size={16} /> Exportar listado</button>
        <button className="sc-btn-primary" onClick={openNew}><Plus size={16} /> Nuevo socio</button>
      </div>

      {/* toolbar: sort + tabs */}
      <div className="sc-toolbar">
        <div className="sc-sort" onClick={stop}>
          <button className="sc-btn-outline" onClick={() => setSortOpen((v) => !v)}>
            <ArrowUpDown size={15} /> Ordenar
          </button>
          {sortOpen && (
            <div className="sc-sort-menu">
              {SORT_OPTIONS.map((o) => (
                <button key={o.key} className={`sc-sort-item${sort === o.key ? " is-active" : ""}`}
                  onClick={() => { setSort(o.key); setSortOpen(false); }}>
                  {o.label}{sort === o.key && <Check size={14} />}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="sc-tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`sc-tab${tab === t.key ? " is-active" : ""}`}
              onClick={() => { setTab(t.key); setPage(1); }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* table */}
      <div className="sc-table">
        <div className="sc-tr sc-thead">
          <div className="sc-td sc-col-socio">Socio</div>
          <div className="sc-td sc-col-num">Nº Socio</div>
          <div className="sc-td sc-col-cad">Caducidad</div>
          <div className="sc-td sc-col-saldo">Saldo</div>
          <div className="sc-td sc-col-estado">Estado</div>
          <div className="sc-td sc-col-acc">Acciones</div>
        </div>
        {pageRows.length === 0 && <div className="sc-empty">No hay socios que coincidan.</div>}
        {pageRows.map((s) => {
          const saldoCls = s.saldo <= 0 ? "sc-saldo--zero" : s.saldo < 2 ? "sc-saldo--low" : "sc-saldo--ok";
          return (
            <div key={s.id} className="sc-tr sc-row" onClick={() => setDetail(s)}>
              <div className="sc-td sc-col-socio">
                <Avatar socio={s} />
                <div className="sc-member">
                  <div className="sc-member-name">{s.name}</div>
                  <div className="sc-member-mail">{s.email}</div>
                </div>
              </div>
              <div className="sc-td sc-col-num">{s.number}</div>
              <div className={`sc-td sc-col-cad${s.expSoon ? " is-soon" : ""}`}>{s.caducidad}</div>
              <div className={`sc-td sc-col-saldo ${saldoCls}`}>{s.saldo <= 0 ? "Sin saldo" : formatEuro(s.saldo)}</div>
              <div className="sc-td sc-col-estado">
                <span className={`sc-estado sc-estado--${s.estado}`}>{ESTADO_LABEL[s.estado]}</span>
              </div>
              <div className="sc-td sc-col-acc" onClick={stop}>
                <button className="sc-kebab" onClick={() => setMenuFor(menuFor === s.id ? null : s.id)} aria-label="Acciones">
                  <MoreVertical size={18} />
                </button>
                {menuFor === s.id && (
                  <div className="sc-actions-menu">
                    <button className="sc-action" onClick={() => openProfile(s)}><Eye size={16} /> Ver perfil</button>
                    <button className="sc-action" onClick={() => { setSaldoFor(s); setMenuFor(null); }}><Wallet size={16} /> Saldo</button>
                    <button className="sc-action sc-action--danger" onClick={() => { setDeleteFor(s); setMenuFor(null); }}><Trash2 size={16} /> Eliminar</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* pagination */}
      <div className="sc-pagination">
        <span className="sc-page-text">Página {safePage} de {pageCount}</span>
        <div className="sc-page-nav">
          <button className="sc-page-btn" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}><ChevronLeft size={16} /></button>
          <button className="sc-page-btn" disabled={safePage <= 1} onClick={() => setPage(1)}><ChevronsLeft size={16} /></button>
          <button className="sc-page-btn" disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}><ChevronRight size={16} /></button>
          <button className="sc-page-btn" disabled={safePage >= pageCount} onClick={() => setPage(pageCount)}><ChevronsRight size={16} /></button>
        </div>
      </div>
    </>
  );
}

/* ═══════════════ DETAIL DRAWER ═══════════════ */
function DetailDrawer({ socio, onClose, onSaldo, onProfile }) {
  return (
    <div className="sc-drawer-scrim" onClick={onClose}>
      <aside className="sc-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="sc-drawer-head">
          <span className="sc-drawer-title">Detalle socio</span>
          <button className="sc-icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sc-drawer-body">
          <div className="sc-drawer-hero">
            <Avatar socio={socio} size={96} />
            <div className="sc-drawer-name">{socio.name}</div>
            <div className="sc-drawer-sub">Nº {socio.number} <span className={`sc-dot sc-dot--${socio.estado}`} /> {ESTADO_LABEL[socio.estado]}</div>
          </div>
          <div className="sc-saldo-card">
            <div className="sc-saldo-label">SALDO DISPONIBLE</div>
            <div className="sc-saldo-value">{formatEuro(socio.saldo)}</div>
          </div>
          <Section title="Contacto">
            <Field label="Teléfono" value={socio.phone} />
            <Field label="Email" value={socio.email} />
            <Field label="Dirección" value={`${socio.address}${socio.city ? ", " + socio.city : ""}`} />
          </Section>
          <Section title="Membresía">
            <Field label="Alta" value={socio.alta} />
            <Field label="Caducidad" value={socio.caducidad} />
            <Field label="Tipo de consumo" value={socio.tipoConsumo} />
            <Field label="Cuota" value={socio.cuota} />
          </Section>
        </div>
        <div className="sc-drawer-foot">
          <button className="sc-btn-outline sc-grow" onClick={onSaldo}>Gestionar saldo</button>
          <button className="sc-btn-primary sc-grow" onClick={onProfile}>Ver perfil completo</button>
        </div>
      </aside>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="sc-drawer-section">
      <div className="sc-section-title">{title}</div>
      <div className="sc-drawer-rows">{children}</div>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div className="sc-drawer-row">
      <span className="sc-drawer-key">{label}</span>
      <span className="sc-drawer-val">{value}</span>
    </div>
  );
}

/* ═══════════════ NUEVO / EDITAR SOCIO ═══════════════ */
function SocioForm({ socio, onCancel, onSave }) {
  const isEdit = !!socio;
  const [f, setF] = useState(() => ({
    firstName: socio?.firstName || "", lastName: socio?.lastName || "",
    dni: socio?.dni || "", birthDate: socio?.birthDate || "",
    phone: socio?.phone || "", email: socio?.email || "", address: socio?.address || "",
    cp: socio?.cp || "", city: socio?.city || "",
    number: socio?.number || 18, avalador: socio?.avalador || "",
    duracion: socio?.duracion || "1 año", alta: socio?.alta || "", caducidad: socio?.caducidad || "",
    cuota: socio?.cuota || "", descuento: socio?.descuento || "Sin descuento",
    lineaCoste: socio?.lineaCoste || "Defecto", tipoConsumo: socio?.tipoConsumo || "",
    consumoMax: socio?.consumoMax ?? 0, notas: socio?.notas || "",
    photo: !!socio?.photo, docFront: isEdit, docBack: isEdit,
  }));
  const [touched, setTouched] = useState({});
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const blur = (k) => () => setTouched((p) => ({ ...p, [k]: true }));

  const required = ["firstName", "lastName", "dni", "birthDate", "phone", "email", "cp", "city", "number", "alta"];
  const missing = (k) => !!touched[k] && !String(f[k] ?? "").trim();
  const valid = required.every((k) => String(f[k] ?? "").trim());

  function submit() {
    if (valid) onSave();
    else setTouched(Object.fromEntries(required.map((k) => [k, true])));
  }

  return (
    <div className="sc-form">
      <div className="sc-crumbs">Socios <span className="sc-crumb-sep">›</span> <span className="sc-crumb-cur">{isEdit ? "Editar socio" : "Nuevo socio"}</span></div>
      <div className="sc-form-grid">
        {/* LEFT */}
        <div className="sc-form-col">
          <div className="sc-card">
            <div className="sc-card-eyebrow">Datos personales</div>
            <div className="sc-photo-box">
              <div className="sc-photo-avatar">{f.photo ? <span className="sc-photo-filled" /> : <Camera size={22} />}</div>
              <div className="sc-photo-actions">
                <div className="sc-photo-btns">
                  <button className="sc-btn-primary sc-btn-sm" onClick={() => setF((p) => ({ ...p, photo: true }))}><Camera size={15} /> Hacer foto</button>
                  <button className="sc-btn-outline sc-btn-sm">Desde dispositivo</button>
                </div>
                <span className="sc-help">Requerida para registrar el socio</span>
              </div>
            </div>
            <div className="sc-field-row">
              <Input label="Nombre *" value={f.firstName} onChange={set("firstName")} onBlur={blur("firstName")} placeholder="Nombre" err={missing("firstName")} />
              <Input label="Apellidos *" value={f.lastName} onChange={set("lastName")} onBlur={blur("lastName")} placeholder="Apellidos" err={missing("lastName")} />
            </div>
            <div className="sc-field-row">
              <Input label="DNI/NIE *" value={f.dni} onChange={set("dni")} onBlur={blur("dni")} placeholder="12345678A" err={missing("dni")} />
              <Input label="Fecha nacimiento *" value={f.birthDate} onChange={set("birthDate")} onBlur={blur("birthDate")} placeholder="dd/mm/aaaa" err={missing("birthDate")} errMsg="Este campo es obligatorio." />
            </div>
          </div>

          <div className="sc-card">
            <div className="sc-card-eyebrow">Contacto</div>
            <div className="sc-field-row">
              <Input label="Teléfono *" value={f.phone} onChange={set("phone")} onBlur={blur("phone")} placeholder="+34 600 000 000" err={missing("phone")} />
              <Input label="Email *" value={f.email} onChange={set("email")} onBlur={blur("email")} placeholder="email@ejemplo.com" err={missing("email")} />
            </div>
            <Input label="Dirección" value={f.address} onChange={set("address")} placeholder="Calle, número, piso…" />
            <div className="sc-field-row">
              <Input label="CP *" value={f.cp} onChange={set("cp")} onBlur={blur("cp")} placeholder="41001" err={missing("cp")} />
              <Input label="Ciudad *" value={f.city} onChange={set("city")} onBlur={blur("city")} placeholder="Ciudad" err={missing("city")} />
            </div>
          </div>

          <div className="sc-card">
            <div className="sc-card-eyebrow">Documentación</div>
            <p className="sc-help sc-help--block">Fotografía del DNI (opcional, recomendado para el expediente).</p>
            <div className="sc-field-row">
              <DocSlot label="Anverso *" filled={f.docFront} filename="dni_anverso.jpg" onUpload={() => setF((p) => ({ ...p, docFront: true }))} />
              <DocSlot label="Reverso *" filled={f.docBack} filename="dni_reverso.jpg" onUpload={() => setF((p) => ({ ...p, docBack: true }))} />
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="sc-form-col">
          <div className="sc-card">
            <div className="sc-card-eyebrow">Membresía</div>
            <div className="sc-field-row">
              <Input label="Nº Socio *" value={f.number} onChange={set("number")} onBlur={blur("number")} placeholder="18" err={missing("number")} />
              <Input label="Socio avalador" value={f.avalador} onChange={set("avalador")} placeholder="Nombre o número de avalador." />
            </div>
            <p className="sc-help sc-help--block">Siguiente disponible: 18. Puedes cambiarlo.</p>
            <label className="sc-label">Duración de inscripción</label>
            <div className="sc-segment">
              {["1 mes", "3 meses", "1 año"].map((d) => (
                <button key={d} className={`sc-segment-btn${f.duracion === d ? " is-active" : ""}`} onClick={() => setF((p) => ({ ...p, duracion: d }))}>{d}</button>
              ))}
            </div>
            <div className="sc-field-row">
              <Input label="Alta *" value={f.alta} onChange={set("alta")} onBlur={blur("alta")} placeholder="dd/mm/aaaa" err={missing("alta")} />
              <Input label="Caducidad" value={f.caducidad} onChange={set("caducidad")} placeholder="dd/mm/aaaa" />
            </div>
            <div className="sc-field-row">
              <Input label="Cuota" value={f.cuota} onChange={set("cuota")} placeholder="ej: Mensual" />
              <Select label="Descuento" value={f.descuento} onChange={set("descuento")} options={["Sin descuento", "10%", "20%", "50%"]} />
            </div>
            <div className="sc-field-row">
              <Input label="Línea de coste" value={f.lineaCoste} onChange={set("lineaCoste")} placeholder="Defecto" />
              <div className="sc-field">
                <label className="sc-label">Tipo de consumo</label>
                <div className="sc-radios">
                  {["Lúdico", "Terapéutico"].map((t) => (
                    <button key={t} className={`sc-radio${f.tipoConsumo === t ? " is-active" : ""}`} onClick={() => setF((p) => ({ ...p, tipoConsumo: t }))}>
                      <span className="sc-radio-dot" />{t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Input label="Consumo máximo / mes" value={f.consumoMax} onChange={set("consumoMax")} placeholder="0" />
            <div className="sc-field">
              <label className="sc-label">Notas</label>
              <textarea className="sc-textarea" value={f.notas} onChange={set("notas")} placeholder="Observaciones internas…" rows={4} />
            </div>
          </div>
        </div>
      </div>

      <div className="sc-form-foot">
        <button className="sc-btn-outline" onClick={onCancel}>Cancelar</button>
        <button className={`sc-btn-primary${valid ? "" : " is-disabled"}`} onClick={submit} disabled={!valid}>
          {isEdit ? "Guardar cambios" : "Registrar socio"}
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, onBlur, placeholder, err, errMsg }) {
  return (
    <div className="sc-field">
      <label className="sc-label">{label}</label>
      <input className={`sc-input${err ? " is-error" : ""}`} value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder} />
      {err && <span className="sc-error">{errMsg || "Este campo es obligatorio."}</span>}
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div className="sc-field">
      <label className="sc-label">{label}</label>
      <div className="sc-select">
        <select value={value} onChange={onChange}>{options.map((o) => <option key={o}>{o}</option>)}</select>
        <ChevronDown size={16} />
      </div>
    </div>
  );
}
function DocSlot({ label, filled, filename, onUpload }) {
  return (
    <div className="sc-field">
      <label className="sc-label">{label}</label>
      {filled ? (
        <div className="sc-doc-chip">
          <span className="sc-doc-thumb" />
          <div className="sc-doc-meta"><span className="sc-doc-name">{filename}</span><span className="sc-doc-size">356 KB</span></div>
          <button className="sc-icon-btn sc-icon-btn--sm"><X size={14} /></button>
        </div>
      ) : (
        <div className="sc-doc-btns">
          <button className="sc-btn-outline sc-btn-sm"><Camera size={14} /> Cámara</button>
          <button className="sc-btn-outline sc-btn-sm" onClick={onUpload}><Upload size={14} /> Subir archivo</button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ PERFIL (read-only) ═══════════════ */
function SocioProfile({ socio, onBack, onEdit, onSaldo, onDelete }) {
  return (
    <div className="sc-profile">
      <div className="sc-crumbs">
        <button className="sc-crumb-link" onClick={onBack}>Socios</button>
        <span className="sc-crumb-sep">›</span>
        <span className="sc-crumb-cur">{socio.lastName || socio.name}{socio.firstName ? `, ${socio.firstName}` : ""}</span>
      </div>

      <div className="sc-profile-hero">
        <Avatar socio={socio} size={72} />
        <div className="sc-profile-id">
          <div className="sc-profile-name">{socio.name}</div>
          <div className="sc-profile-sub">Nº Socio: {socio.number} <span className={`sc-dot sc-dot--${socio.estado}`} /> {ESTADO_LABEL[socio.estado]}</div>
          <div className="sc-profile-dates">Alta: {socio.alta} · Caducidad: {socio.caducidad}</div>
        </div>
        <div className="sc-profile-actions">
          <div className="sc-profile-btns">
            <button className="sc-btn-outline" onClick={onSaldo}>Gestionar saldo</button>
            <button className="sc-btn-primary" onClick={onEdit}>Editar</button>
          </div>
          <div className="sc-saldo-card sc-saldo-card--sm">
            <div className="sc-saldo-label">SALDO DISPONIBLE</div>
            <div className="sc-saldo-value">{formatEuro(socio.saldo)}</div>
          </div>
        </div>
      </div>

      <div className="sc-form-grid">
        <div className="sc-form-col">
          <div className="sc-card">
            <div className="sc-card-eyebrow">Datos personales</div>
            <div className="sc-photo-box sc-photo-box--ro"><Avatar socio={socio} size={56} /><span className="sc-ro-photo-label">Foto del socio</span></div>
            <div className="sc-field-row"><RoField label="Nombre" value={socio.firstName || socio.name} /><RoField label="Apellidos" value={socio.lastName || "—"} /></div>
            <div className="sc-field-row"><RoField label="DNI/NIE" value={socio.dni} /><RoField label="Fecha nacimiento" value={socio.birthDate} /></div>
          </div>
          <div className="sc-card">
            <div className="sc-card-eyebrow">Contacto</div>
            <div className="sc-field-row"><RoField label="Teléfono" value={socio.phone} /><RoField label="Email" value={socio.email} /></div>
            <RoField label="Dirección" value={socio.address} />
            <div className="sc-field-row"><RoField label="CP" value={socio.cp} /><RoField label="Ciudad" value={socio.city} /></div>
          </div>
        </div>
        <div className="sc-form-col">
          <div className="sc-card">
            <div className="sc-card-eyebrow">Membresía</div>
            <div className="sc-field-row"><RoField label="Nº Socio" value={socio.number} /><RoField label="Socio avalador" value={socio.avalador} /></div>
            <p className="sc-help sc-help--block">Siguiente disponible: 18. Puedes cambiarlo.</p>
            <label className="sc-label">Duración de inscripción</label>
            <div className="sc-segment sc-segment--ro"><span className="sc-segment-btn is-active">{socio.duracion}</span></div>
            <div className="sc-field-row"><RoField label="Alta" value={socio.alta} /><RoField label="Caducidad" value={socio.caducidad} /></div>
            <div className="sc-field-row"><RoField label="Cuota" value={socio.cuota} /><RoField label="Descuento" value={socio.descuento} /></div>
            <div className="sc-field-row"><RoField label="Línea de coste" value={socio.lineaCoste} /><RoField label="Tipo de consumo" value={socio.tipoConsumo} /></div>
            <RoField label="Consumo máximo / mes" value={socio.consumoMax} />
            <RoField label="Notas" value={socio.notas || "—"} />
          </div>
        </div>
      </div>

      <div className="sc-profile-foot">
        <button className="sc-danger-link" onClick={onDelete}>Eliminar socio</button>
      </div>
    </div>
  );
}
function RoField({ label, value }) {
  return (
    <div className="sc-field">
      <label className="sc-label">{label}</label>
      <div className="sc-ro-value">{value === "" || value == null ? "—" : value}</div>
    </div>
  );
}

/* ═══════════════ QR PREINSCRIPCIÓN ═══════════════ */
function QrModal({ url, onClose, onCopied, onRotated }) {
  const qrRef = useRef(null);
  function copy() { navigator.clipboard?.writeText(url); onCopied(); }
  function download() {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const blob = new Blob([svg.outerHTML], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "qr-preinscripcion.svg"; a.click();
  }
  return (
    <div className="sc-scrim" onClick={onClose}>
      <div className="sc-modal sc-modal--qr" onClick={(e) => e.stopPropagation()}>
        <button className="sc-modal-close" onClick={onClose}><X size={18} /></button>
        <h2 className="sc-modal-title">QR de Preinscripción</h2>
        <p className="sc-modal-sub">Muestra o imprime este código para que los socios potenciales rellenen su solicitud.</p>
        <div className="sc-qr-box" ref={qrRef}><QRCode value={url} size={200} /></div>
        <p className="sc-qr-url">{url}</p>
        <div className="sc-modal-btns">
          <button className="sc-btn-primary sc-grow" onClick={copy}>Copiar enlace</button>
          <button className="sc-btn-outline sc-grow" onClick={download}>Descargar QR</button>
          <button className="sc-btn-danger-subtle sc-grow" onClick={onRotated}>Nuevo QR</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ ELIMINAR ═══════════════ */
function DeleteDialog({ socio, onCancel, onConfirm }) {
  return (
    <div className="sc-scrim" onClick={onCancel}>
      <div className="sc-modal sc-modal--confirm" onClick={(e) => e.stopPropagation()}>
        <h2 className="sc-modal-title sc-modal-title--sm">¿Eliminar socio?</h2>
        <p className="sc-modal-sub">Se eliminará el socio y todo su registro. Esta acción no se puede deshacer.</p>
        <div className="sc-modal-btns sc-modal-btns--confirm">
          <button className="sc-btn-danger-subtle sc-grow" onClick={onConfirm}>Eliminar</button>
          <button className="sc-btn-primary sc-grow" onClick={onCancel}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ GESTIÓN DE SALDO ═══════════════ */
const SALDO_METHODS = ["Tarjeta", "Efectivo", "Transferencia"];
const QUICK = [10, 20, 50, 100];
function SaldoDialog({ socio, onClose, onDone }) {
  const [mode, setMode] = useState("add");         // add | withdraw
  const [method, setMethod] = useState("Tarjeta");
  const [amount, setAmount] = useState(0);
  const bal = socio.saldo;
  const next = mode === "add" ? bal + Number(amount || 0) : Math.max(0, bal - Number(amount || 0));
  const overdraw = mode === "withdraw" && Number(amount || 0) > bal;
  const cta = mode === "add" ? (method === "Efectivo" ? "Cobrar en efectivo" : method === "Transferencia" ? "Registrar transferencia" : "Cobrar con tarjeta") : "Retirar saldo";

  return (
    <div className="sc-scrim" onClick={onClose}>
      <div className="sc-modal sc-modal--saldo" onClick={(e) => e.stopPropagation()}>
        <button className="sc-modal-close" onClick={onClose}><X size={18} /></button>
        <h2 className="sc-modal-title sc-modal-title--sm">Gestión de saldo</h2>
        <p className="sc-modal-sub sc-modal-sub--left">{socio.name} · Nº {socio.number}</p>
        <div className="sc-saldo-tabs">
          <button className={`sc-saldo-tab${mode === "add" ? " is-active" : ""}`} onClick={() => { setMode("add"); setAmount(0); }}>Agregar saldo</button>
          <button className={`sc-saldo-tab${mode === "withdraw" ? " is-active" : ""}`} onClick={() => { setMode("withdraw"); setAmount(0); }}>Retirar saldo</button>
        </div>

        {mode === "add" ? (
          <div className="sc-saldo-methods">
            {SALDO_METHODS.map((m) => (
              <button key={m} className={`sc-chip${method === m ? " is-active" : ""}`} onClick={() => setMethod(m)}>{m}</button>
            ))}
          </div>
        ) : (
          <div className="sc-saldo-avail">
            <span>Saldo disponible</span>
            <button className="sc-link" onClick={() => setAmount(bal)}>Retirar todo</button>
          </div>
        )}

        <div className="sc-quick">
          {QUICK.map((q) => (
            <button key={q} className={`sc-chip${Number(amount) === q ? " is-active" : ""}`} disabled={mode === "withdraw" && q > bal} onClick={() => setAmount(q)}>€{q}</button>
          ))}
        </div>
        <div className="sc-field">
          <label className="sc-label">Importe</label>
          <div className="sc-select"><input className="sc-input" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" /></div>
        </div>
        <div className="sc-saldo-preview">
          <span>Saldo actual</span>
          <span className="sc-saldo-arrow">{formatEuro(bal)} → <strong className={overdraw ? "is-over" : ""}>{formatEuro(next)}</strong></span>
        </div>
        <div className="sc-modal-btns">
          <button className={`sc-grow${mode === "add" ? " sc-btn-primary" : " sc-btn-outline"}`} disabled={!Number(amount) || overdraw}
            onClick={() => onDone(mode === "add" ? "Saldo añadido" : "Saldo retirado")}>{cta}</button>
        </div>
      </div>
    </div>
  );
}
